import type { Service, ServiceCategory as Category } from '@/entities/service/model/types';

export interface ImportStats {
    updated: number;
    created: number;
    newCatsCount: number;
    /** Matched (update) rows whose incoming price was blank and whose existing price was kept.
     *  SAFE-03 (T-34-18): excludes created services — a created service does not "keep" a price,
     *  it gets the create branch's own zero fallback. */
    blankKept: number;
}

export interface MergeResult {
    services: Service[];
    newCategories: Category[];
    stats: ImportStats;
}

/**
 * Логика импорта и слияния данных услуг из Excel/JSON.
 */
export interface IncomingServiceData {
    name: string;
    priceLabor?: number;
    category?: string;
    unitType?: string;
}

export const mergeServicesData = (
    currentServices: Service[], 
    incomingData: IncomingServiceData[], 
    currentCategories: Category[]
): MergeResult => {
  let updatedCount = 0;
  let createdCount = 0;
  let blankKeptCount = 0;
  const newCats: Category[] = [];

  const currentCatNames = new Set(currentCategories.map(c => c.name));
  const incomingCatNames = new Set(incomingData.map(s => s.category));
  
  incomingCatNames.forEach(catName => {
      if (catName && !currentCatNames.has(catName)) {
          newCats.push({ 
              id: `cat_${Date.now()}_${Math.random()}`, 
              name: catName
          } as Category);
          currentCatNames.add(catName);
      }
  });

  const servicesMap = new Map<string, Service>(currentServices.map(s => [s.name.trim().toLowerCase(), s]));
  
  incomingData.forEach(incoming => {
      const key = incoming.name.trim().toLowerCase();
      if (servicesMap.has(key)) {
          const existing = servicesMap.get(key)!;
          // SAFE-03 (T-34-16): treat the incoming price as blank when it is not a finite number —
          // absent, null, or a cell that parsed to NaN all count. A blank price keeps the existing
          // service's price rather than writing an absent value into a required numeric field
          // (Service.priceLabor is required; IncomingServiceData.priceLabor is optional — writing
          // it through unchecked was an arithmetic NaN hazard, worse than a visible zero).
          const priceBlank = !Number.isFinite(incoming.priceLabor);
          if (priceBlank) blankKeptCount++;
          // SAFE-03 (T-34-17): mirror the create branch below — set both unit fields together
          // when the incoming row carries a unit, otherwise keep both from the existing service.
          // The old code set neither, contradicting its own comment ("unit stays same or updated").
          const updated: Service = {
              ...existing,
              priceLabor: priceBlank ? existing.priceLabor : incoming.priceLabor as number,
              unit: incoming.unitType || existing.unit,
              unitType: incoming.unitType || existing.unitType,
              categoryId: existing.categoryId, // SAFE-03: deliberately kept — see plan note on categories
          };
          servicesMap.set(key, updated);
          updatedCount++;
      } else {
          const newItem: Service = {
              id: `imp_${Date.now()}_${Math.random()}`,
              name: incoming.name,
              categoryId: 'other', 
              unit: incoming.unitType || 'pcs',
              unitType: incoming.unitType || 'pcs',
              priceLabor: incoming.priceLabor || 0,
              adjustment: 1,
              isCustom: false
          };
          servicesMap.set(key, newItem);
          createdCount++;
      }
  });

  return {
      services: Array.from(servicesMap.values()),
      newCategories: newCats,
      stats: { updated: updatedCount, created: createdCount, newCatsCount: newCats.length, blankKept: blankKeptCount }
  };
};
